import React, { useCallback, useEffect, useState } from 'react';
import classNames from 'classnames';
import { useIntl } from 'react-intl';

import config from 'config';

import { useCurrentVideo, useNextVideo } from 'hooks/video';
import { isLiveVideo, isVodContent } from 'support/videoHelper';
import textToSpeechManager from 'support/tts/TextToSpeechManager';

import { usePlayerState } from 'components/providers/PlayerStateContext';

import liveDotSrc from 'assets/images/livedot.webp';

import NotificationOverlay from './NotificationOverlay';

import styles from './PlayerNotification.module.scss';
import { VideoLike } from 'store-types';

const messages = {
  upNext: {
    id: 'notification.upNext',
    description: 'Up next label',
  },
  live: {
    id: 'notification.live',
    description: 'TTS for Live bug',
  },
  nowPlaying: {
    id: 'notification.nowPlaying',
    description: 'TTS for now playing',
  },
};

interface PlayerNotificationProps {
  progress: number;
  duration: number;
}

const PlayerNotification = ({ progress, duration }: PlayerNotificationProps) => {
  const intl = useIntl();
  const [ttsText, setTtsText] = useState('');
  const [nextVideo] = useNextVideo() ?? [];
  const [video, row] = useCurrentVideo();
  const { isAdActive } = usePlayerState() ?? {};
  const isVod = isVodContent(video as VideoLike);

  useEffect(() => {
    if (!ttsText) return;
    textToSpeechManager.add(ttsText);
    textToSpeechManager.speak();
  }, [ttsText]);

  const updateTts = useCallback(
    (tts: (string | null | undefined)[]) => {
      const text = tts.filter(item => item || null).join(', ');
      if (ttsText !== text) setTtsText(text);
    },
    [ttsText],
  );

  const {
    nowPlayingMsgDurationMs,
    upNextMsgBeforeVideoEndMs,
  } = config.playback;

  const showNotification = !isAdActive && isVod;
  const showUpNext = (duration - progress) * 1000 <= upNextMsgBeforeVideoEndMs;
  const showNowPlaying = progress * 1000 <= nowPlayingMsgDurationMs;

  useEffect(() => {
    let tts: (string | null | undefined)[] = [];
    if (showNotification) {
      if (showUpNext) {
        const isNextLive = isLiveVideo(nextVideo as VideoLike);
        const { shortTitle: nextTitle } = nextVideo?.about || {};
        tts = [
          intl.formatMessage(messages.upNext),
          isNextLive ? intl.formatMessage(messages.live) : null,
          nextTitle,
        ];
      } else if (showNowPlaying && progress > 0) {
        const rowTitle = row?.title;
        const { shortTitle } = video?.about ?? {};
        tts = [intl.formatMessage(messages.nowPlaying), rowTitle, shortTitle];
      }
    }
    updateTts(tts);
  });

  if (!showNotification) {
    return null;
  }

  if (showUpNext) {
    const isNextLive = isLiveVideo(nextVideo as VideoLike);
    const { shortTitle: nextTitle } = nextVideo?.about ?? {};
    return (
      <NotificationOverlay>
        <div className={styles.label}>
          {isNextLive && (
            <img
              className={styles.liveDot}
              alt="live dot"
              data-testid="live-dot"
              src={liveDotSrc}
            />
          )}
          <span>{intl.formatMessage(messages.upNext)}</span>
        </div>
        <div className={classNames('text-truncate', styles.title)}>
          {nextTitle}
        </div>
      </NotificationOverlay>
    );
  }

  if (showNowPlaying) {
    const rowTitle = row?.title;
    const { shortTitle } = video?.about ?? {};
    return (
      <NotificationOverlay>
        {rowTitle && <div className={styles.rowTitle}>{rowTitle}</div>}
        <div className={classNames('text-truncate', styles.title)}>
          {shortTitle}
        </div>
      </NotificationOverlay>
    );
  }

  return null;
};

export default PlayerNotification;
